<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import { useToastStore } from '@uiKit/store/toastStore'
import { logger } from '@core/logging/logger'
import { downloadBlob } from '@shared/utils'
import OrderDocumentRow from '../components/OrderDocumentRow.vue'
import OrderDocumentPreview from '../components/OrderDocumentPreview.vue'
import { useOrderReceipt } from '../composables/useOrderReceipt'
import { useOrderTitleDeed } from '../composables/useOrderTitleDeed'
import { isClosedStatus } from '../constants/orderStatus'
import {
  ORDER_DOCUMENT_LABEL,
  ORDER_DOCUMENT_TOAST,
  type OrderDocumentKind,
} from '../constants/orderDocuments'
import type { OrderDocumentFile } from '../types/orderTypes'

// The order's documents — Order Receipt and Title Deed Certificate — with one
// preview + download flow. Mounted by the invest drawer's Order Confirmed
// step, the order detail page and the holding page so all three behave the
// same. Renders nothing when there is nothing to show (no placeholder row).

interface Props {
  orderId: string
  orderReference: string
  /** Order displayStatus. Unknown / not loaded counts as "certificate may still come". */
  displayStatus?: string | null
  showReceipt?: boolean
  /** Re-read the documents list every 10 s for 60 s until the certificate lands. */
  pollTitleDeed?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  displayStatus: null,
  showReceipt: true,
  pollTitleDeed: false,
})

const emit = defineEmits<{
  /** Whether any row is showing — lets a host hide its own section chrome. */
  (e: 'availability-change', hasDocuments: boolean): void
}>()

const toastStore = useToastStore()

// Rejected / cancelled / deleted orders never get a certificate: read the
// list once, don't poll.
const isClosedOrder = computed(() => isClosedStatus(props.displayStatus))

const receipt = useOrderReceipt(
  () => props.orderId,
  () => props.orderReference,
)
const titleDeed = useOrderTitleDeed(
  () => props.orderId,
  () => props.orderReference,
  () => props.pollTitleDeed && !isClosedOrder.value,
)

const sources = { receipt, titleDeed } satisfies Record<
  OrderDocumentKind,
  { load: () => Promise<OrderDocumentFile> }
>

const showsReceipt = computed(() => props.showReceipt && receipt.isAvailable.value)
const showsTitleDeed = computed(() => titleDeed.isAvailable.value)
const hasDocuments = computed(() => showsReceipt.value || showsTitleDeed.value)

watch(hasDocuments, (value) => emit('availability-change', value), { immediate: true })

// ── Preview ───────────────────────────────────────────────────────────────
const activeKind = ref<OrderDocumentKind | null>(null)
const previewFile = ref<OrderDocumentFile | null>(null)
/** Set when the certificate's blob fetch was blocked: preview the public link instead. */
const directUrl = ref('')
const isLoading = ref(false)
const hasError = ref(false)

const open = async (kind: OrderDocumentKind) => {
  if (isLoading.value) return
  activeKind.value = kind
  previewFile.value = null
  directUrl.value = ''
  hasError.value = false
  isLoading.value = true
  try {
    const file = await sources[kind].load()
    // Ignore a late answer for a preview the user already closed or switched.
    if (activeKind.value === kind) previewFile.value = file
  } catch (error) {
    if (activeKind.value !== kind) return
    // The certificate is a public CDN link. When the browser blocks reading
    // it as a blob (typically the CDN sends no CORS headers), show the link
    // itself rather than an error — the file is there, only JS can't read it.
    if (kind === 'titleDeed' && titleDeed.url.value) {
      logger.warn(
        'Title deed: blob fetch failed, previewing the public link instead. If this is CORS, the CDN must allow this origin.',
        error,
      )
      directUrl.value = titleDeed.url.value
    } else {
      hasError.value = true
    }
  } finally {
    isLoading.value = false
  }
}

const close = () => {
  activeKind.value = null
  previewFile.value = null
  directUrl.value = ''
  hasError.value = false
}

const retry = () => {
  if (activeKind.value) open(activeKind.value)
}

const download = () => {
  const file = previewFile.value
  const kind = activeKind.value
  if (!kind) return
  // No readable blob: a cross-origin `download` attribute is ignored by the
  // browser, so hand the public link to a new tab where it can be saved.
  if (!file) {
    if (directUrl.value) window.open(directUrl.value, '_blank', 'noopener,noreferrer')
    return
  }
  try {
    downloadBlob(file.blob, file.fileName)
    toastStore.addToast({ type: 'success', message: ORDER_DOCUMENT_TOAST[kind] })
  } catch {
    toastStore.addToast({ type: 'error', message: ORDER_DOCUMENT_TOAST.failed })
  }
}

// The certificate is a public CDN link — opening it directly is the accepted
// fallback when the browser blocks the blob fetch (CORS).
const openFallback = () => {
  if (titleDeed.url.value) window.open(titleDeed.url.value, '_blank', 'noopener,noreferrer')
}
</script>

<template>
  <Box v-if="hasDocuments" direction="col" gap="3" class="odc">
    <OrderDocumentRow
      v-if="showsReceipt"
      :label="ORDER_DOCUMENT_LABEL.receipt"
      icon="file"
      :size-bytes="receipt.sizeBytes.value"
      :is-loading="isLoading && activeKind === 'receipt'"
      @open="open('receipt')"
    />
    <OrderDocumentRow
      v-if="showsTitleDeed"
      :label="ORDER_DOCUMENT_LABEL.titleDeed"
      icon="verified"
      :size-bytes="titleDeed.sizeBytes.value"
      :is-loading="isLoading && activeKind === 'titleDeed'"
      @open="open('titleDeed')"
    />
  </Box>

  <OrderDocumentPreview
    :visible="activeKind !== null"
    :title="activeKind ? ORDER_DOCUMENT_LABEL[activeKind] : ''"
    :file="previewFile"
    :is-loading="isLoading"
    :has-error="hasError"
    :direct-url="directUrl"
    :has-fallback="activeKind === 'titleDeed' && Boolean(titleDeed.url.value)"
    @close="close"
    @download="download"
    @retry="retry"
    @open-fallback="openFallback"
  />
</template>

<style scoped>
.odc {
  width: 100%;
}
</style>
